import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface HelicopterCapability {
  id: string;
  manufacturer: string;
  model: string;
  icon_url: string | null;
}

interface EngineCapability {
  id: string;
  manufacturer: string;
  model: string;
}

interface ServiceCapabilities {
  helicopters: HelicopterCapability[];
  engines: EngineCapability[];
  loading: boolean;
}

/**
 * Hook to fetch helicopter and engine capabilities for services
 * Uses React Query for global caching - fetches once and reuses
 */
export function useServiceCapabilities(
  helicopterIds: string[] = [],
  engineIds: string[] = []
): ServiceCapabilities {
  // Fetch helicopters by IDs
  const { data: helicopterData = [], isLoading: loadingHelicopters } = useQuery({
    queryKey: ['service-helicopters', helicopterIds],
    queryFn: async () => {
      if (!helicopterIds.length) return [];
      const { data, error } = await supabase
        .from('helicopters')
        .select('id, manufacturer, model, icon_url')
        .in('id', helicopterIds);
      if (error) {
        console.error('Error fetching helicopter capabilities:', error);
        return [];
      }
      return data || [];
    },
    enabled: helicopterIds.length > 0,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000,
  });

  // Fetch engines by IDs - from engines table (helicopter engine types)
  // Also try engine_listings if IDs don't match engines table
  const { data: engineData = [], isLoading: loadingEngines } = useQuery({
    queryKey: ['service-engines', engineIds],
    queryFn: async () => {
      if (!engineIds.length) return [];
      
      // First try the engines table (engine types linked to helicopters)
      const { data: enginesData, error: enginesError } = await supabase
        .from('engines')
        .select('id, engine_name, helicopter_id')
        .in('id', engineIds);
      
      if (!enginesError && enginesData && enginesData.length > 0) {
        // Map engine data to expected format
        return enginesData.map(engine => ({
          id: engine.id,
          manufacturer: '',
          model: engine.engine_name,
        }));
      }
      
      // Fallback: try engine_listings table (standalone engine listings)
      const { data: listingsData, error: listingsError } = await supabase
        .from('engine_listings')
        .select('id, manufacturer, model')
        .in('id', engineIds);
      
      if (listingsError) {
        console.error('Error fetching engine capabilities:', listingsError);
        return [];
      }
      
      return (listingsData || []).map(engine => ({
        id: engine.id,
        manufacturer: engine.manufacturer || '',
        model: engine.model || '',
      }));
    },
    enabled: engineIds.length > 0,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  return {
    helicopters: helicopterData as HelicopterCapability[],
    engines: engineData as EngineCapability[],
    loading: loadingHelicopters || loadingEngines,
  };
}
